import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Trash2, Printer, FileText, Loader2, IdCard } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { v: "admin", l: "Administrator" },
  { v: "staff", l: "Kinomitarbeiter" },
  { v: "snack", l: "Snackpersonal" },
];

const ROLE_BADGE = { admin: "Leitung", staff: "Kinomitarbeiter", snack: "Snackbar", door: "Einlass" };

function BadgeButtons({ user, allUsers }) {
  const [busy, setBusy] = useState(false);
  const build = () => {
    if (user) {
      return { badges: [{ name: user.name || (user.email || "").split("@")[0] || "—", role: ROLE_BADGE[user.role] || "Team" }] };
    }
    return { include_all_staff: true };
  };
  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/signage/name-badges", build(), { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };
  const printOff = async () => {
    const label = user ? `Namensschild für „${user.name || user.email}"` : `alle Team-Schilder (${allUsers?.length || 0} Personen)`;
    if (!window.confirm(`${label} an den Bürodrucker senden?`)) return;
    setBusy(true);
    try {
      await api.post("/admin/signage/name-badges?print_office=true", build());
      toast.success("An Drucker gesendet");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bürodrucker nicht erreichbar"); }
    finally { setBusy(false); }
  };
  return (
    <div className="inline-flex rounded border border-[#2A2A2A] overflow-hidden">
      <button onClick={preview} disabled={busy}
        data-testid={user ? `user-badge-preview-${user.id}` : "user-badge-preview-all"}
        title="Namensschild als PDF anzeigen"
        className="text-xs px-2 py-1 text-[#8FBC8F] hover:bg-[#8FBC8F]/10 disabled:opacity-40 flex items-center gap-1">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
      </button>
      <button onClick={printOff} disabled={busy}
        data-testid={user ? `user-badge-print-${user.id}` : "user-badge-print-all"}
        title="Namensschild an Bürodrucker senden"
        className="text-xs px-2 py-1 text-[#D4AF37] border-l border-[#2A2A2A] hover:bg-[#D4AF37]/10 disabled:opacity-40 flex items-center gap-1">
        <Printer size={12} />
      </button>
    </div>
  );
}

export default function Users() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "staff" });
  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form); toast.success("Benutzer erstellt"); setOpen(false);
      setForm({ email: "", password: "", name: "", role: "staff" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (u) => { if (!window.confirm(`Benutzer ${u.email} löschen?`)) return; await api.delete(`/users/${u.id}`); load(); };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Team</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Benutzer</h1>
        </div>
        <div className="flex items-center gap-2">
          <BadgeButtons allUsers={rows} />
          <button data-testid="user-add-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82]"><Plus size={16} /> Neuer Benutzer</button>
        </div>
      </div>
      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-[#0A0A0A] border-b border-[#1A1A1A] text-[#A8A398] text-xs uppercase tracking-widest">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">E-Mail</th><th className="text-left p-3">Rolle</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-b border-[#141414]">
                <td className="p-3">{u.name}</td>
                <td className="p-3 text-[#A8A398]">{u.email}</td>
                <td className="p-3"><span className="text-[10px] uppercase tracking-widest border border-[#2A2A2A] rounded-full px-2 py-0.5 whitespace-nowrap">{ROLES.find((r) => r.v === u.role)?.l || u.role}</span></td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <BadgeButtons user={u} />
                    <button onClick={() => remove(u)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm p-4 overflow-y-auto flex items-start justify-center">
          <div className="w-full max-w-md bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl my-3 sm:my-6">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#1A1A1A]">
              <div className="font-heading text-2xl">Benutzer</div><button onClick={() => setOpen(false)}><X /></button>
            </div>
            <div className="p-6 grid grid-cols-1 gap-3">
              <F label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} />
              <F label="E-Mail" v={form.email} on={(v) => setForm({ ...form, email: v })} type="email" />
              <F label="Passwort" v={form.password} on={(v) => setForm({ ...form, password: v })} type="password" />
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Rolle</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none">
                  {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#1A1A1A] flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-[#A8A398]">Abbrechen</button>
              <button data-testid="user-save-btn" onClick={create} className="bg-[#D4AF37] text-black px-6 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82]">Erstellen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function F({ label, v, on, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
    </div>
  );
}
